import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { XMarkIcon, PlusIcon } from '@heroicons/react/20/solid';
import { apiClient } from '../services/apiClient';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Bars3Icon } from '@heroicons/react/24/outline';

const ROLE_LABELS = {
  dentist: 'Dentiste',
  pediatricdentist: 'Dentiste pédiatrique',
  'medident-ne': 'Medident Neuchâtel',
  'medident-be': 'Medident Bern',
  generalist: 'Généraliste',
  hygienist: 'Hygiéniste',
  insurance: 'Assurance',
  orthodentist: 'Orthodontiste',
  parodontologist: 'Parodontologiste',
  pediatrician: 'Pédiatre',
  vet: 'Vétérinaire',
};

const getRoleLabel = (role) => ROLE_LABELS[role?.toLowerCase()] || role;

const getUserLabel = (user) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name ? `${name} (${user.email})` : user.email;
};

const userHasSubRole = (user, subRole, roleMappings) => {
  const userGroup = user?.group?.toLowerCase();
  const targetSubRole = subRole?.toLowerCase();
  if (!userGroup || !targetSubRole) return false;

  return roleMappings.some(
    (mapping) =>
      (mapping.role.toLowerCase() === userGroup || mapping.sub_role.toLowerCase() === userGroup) &&
      mapping.sub_role.toLowerCase() === targetSubRole
  );
};

const getEligibleUsersForRole = (users, subRole, roleMappings) =>
  users.filter((user) => user.clinic && userHasSubRole(user, subRole, roleMappings));

export default function KeywordsAdmin() {
  const [roles, setRoles] = useState([]);
  const [roleMappings, setRoleMappings] = useState([]);
  const [users, setUsers] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);
  const [openApplyAllDialog, setOpenApplyAllDialog] = useState(false);
  const [openRoleErrorDialog, setOpenRoleErrorDialog] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyingToAll, setApplyingToAll] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [existingItemKeyword, setExistingItemKeyword] = useState('');
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState('');
  const [editingKeywordItem, setEditingKeywordItem] = useState(null);
  const [editingKeywordValue, setEditingKeywordValue] = useState('');

  const resetFormState = () => {
    setNewKeyword('');
    setExistingItemKeyword('');
    setEditingDescription('');
    setEditingId(null);
    setEditingDescriptionId(null);
    setEditingDescriptionValue('');
    setEditingKeywordItem(null);
    setEditingKeywordValue('');
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      setSuccessMessage('');
    }, 3000);
  };

  useEffect(() => {
    async function fetchRoles() {
      try {
        const [subRoles, mappings] = await Promise.all([
          apiClient.get('/api/admin/roles/sub-roles'),
          apiClient.get('/api/admin/roles'),
        ]);
        setRoles(subRoles);
        setRoleMappings(mappings);
        if (subRoles.length > 0) {
          setSelectedRole(subRoles[0]);
        }
      } catch (err) {
        setError('Erreur lors du chargement des rôles.');
      } finally {
        setRolesLoading(false);
      }
    }

    async function fetchUsers() {
      try {
        const data = await apiClient.get('/api/admin/okta-users');
        const sortedUsers = [...data].sort((a, b) => getUserLabel(a).localeCompare(getUserLabel(b), 'fr'));
        setUsers(sortedUsers);
      } catch (err) {
        setError('Erreur lors du chargement des utilisateurs.');
      } finally {
        setUsersLoading(false);
      }
    }

    fetchRoles();
    fetchUsers();
  }, []);

  const fetchKeywords = useCallback(async (role) => {
    if (!role) return;

    try {
      setKeywordsLoading(true);
      const data = await apiClient.get(`/api/admin/keywords-admin/${role}`);
      setKeywords(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching keywords:', err);
      setError('Erreur lors du chargement des mots clés.');
    } finally {
      setKeywordsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRole) fetchKeywords(selectedRole);
  }, [selectedRole, fetchKeywords]);

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    resetFormState();
  };

  const handleOpenApplyDialog = () => {
    if (!selectedUser || !selectedRole) return;

    if (!userHasSubRole(selectedUser, selectedRole, roleMappings)) {
      setOpenRoleErrorDialog(true);
      return;
    }

    setOpenApplyDialog(true);
  };

  const handleCloseApplyDialog = () => {
    setOpenApplyDialog(false);
  };

  const handleCloseRoleErrorDialog = () => {
    setOpenRoleErrorDialog(false);
  };

  const eligibleUsersForRole = getEligibleUsersForRole(users, selectedRole, roleMappings);

  const handleOpenApplyAllDialog = () => {
    if (!selectedRole || eligibleUsersForRole.length === 0) return;
    setOpenApplyAllDialog(true);
  };

  const handleCloseApplyAllDialog = () => {
    setOpenApplyAllDialog(false);
  };

  const handleApplyKeywordsToAll = async () => {
    if (!selectedRole || eligibleUsersForRole.length === 0) return;

    try {
      setApplyingToAll(true);
      setError(null);

      const clinicIds = [...new Set(eligibleUsersForRole.map((user) => user.clinic))];
      const result = await apiClient.post('/api/admin/keywords-admin/apply-to-all', {
        role: selectedRole,
        clinicIds,
      });

      handleCloseApplyAllDialog();
      if (result.appliedClinics > 0) {
        showSuccess(
          `Mots-clés du rôle "${getRoleLabel(selectedRole)}" appliqués à ${result.appliedClinics} clinique(s).`
        );
      } else {
        showSuccess('Aucune clinique trouvée en base : aucun mot-clé n\'a été modifié.');
      }
    } catch (err) {
      console.error('Error applying keywords to all users:', err);
      const message = err.response?.data?.message
        || err.response?.data?.error
        || err.message
        || "Erreur lors de l'application des mots-clés à tous les utilisateurs.";
      setError(message);
    } finally {
      setApplyingToAll(false);
    }
  };

  const handleApplyKeywordsToUser = async () => {
    if (!selectedUser?.clinic || !selectedRole) return;

    try {
      setApplying(true);
      setError(null);

      const result = await apiClient.post('/api/admin/keywords-admin/apply-to-user', {
        role: selectedRole,
        clinicId: selectedUser.clinic,
      });

      handleCloseApplyDialog();
      if (result.applied) {
        showSuccess(`Mots-clés du rôle "${getRoleLabel(selectedRole)}" appliqués à ${getUserLabel(selectedUser)}.`);
      } else {
        showSuccess(`Aucune clinique trouvée en base pour ${getUserLabel(selectedUser)} : aucun mot-clé n'a été modifié.`);
      }
    } catch (err) {
      console.error('Error applying keywords to user:', err);
      const message = err.response?.data?.message
        || err.response?.data?.error
        || err.message
        || "Erreur lors de l'application des mots-clés à l'utilisateur.";
      setError(message);
    } finally {
      setApplying(false);
    }
  };

  const handleAddKeyword = async (description = '') => {
    if (!description && !editingDescription) return;
    try {
      setSaving(true);
      setError(null);
      const nextRank = keywords.length > 0 ? Math.max(...keywords.map((k) => k.rank ?? 0)) + 1 : 0;
      const newItem = await apiClient.post('/api/admin/keywords-admin', {
        role: selectedRole,
        description: description || editingDescription,
        keywords: newKeyword,
        rank: nextRank,
      });
      showSuccess('Mot clé ajouté avec succès !');
      setNewKeyword('');
      setEditingDescription('');
      await fetchKeywords(selectedRole);
      setTimeout(() => {
        const input = document.getElementById(`keyword-input-${newItem.id}`);
        if (input) {
          setEditingId(newItem.id);
          input.focus();
        }
      }, 100);
    } catch (err) {
      console.error('Error adding keyword:', err);
      setError("Erreur lors de l'ajout du mot clé.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateKeywords = async (id, updatedKeywords) => {
    try {
      setSaving(true);
      setError(null);

      await apiClient.patch(`/api/admin/keywords-admin/${id}`, {
        keywords: updatedKeywords,
      });

      fetchKeywords(selectedRole);
    } catch (err) {
      console.error('Error updating keywords:', err);
      setError('Erreur lors de la modification.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDescription = async (id, updatedDescription) => {
    try {
      setSaving(true);
      setError(null);

      await apiClient.patch(`/api/admin/keywords-admin/${id}`, {
        description: updatedDescription,
      });

      fetchKeywords(selectedRole);
    } catch (err) {
      console.error('Error updating description:', err);
      setError('Erreur lors de la modification.');
    } finally {
      setSaving(false);
    }
  };

  const handleDescriptionDoubleClick = (item) => {
    setEditingDescriptionId(item.id);
    setEditingDescriptionValue(item.description);
  };

  const handleDescriptionBlur = async (item) => {
    if (editingDescriptionValue.trim() && editingDescriptionValue !== item.description) {
      await handleUpdateDescription(item.id, editingDescriptionValue.trim());
    }
    setEditingDescriptionId(null);
    setEditingDescriptionValue('');
  };

  const handleDescriptionKeyDown = (e, item) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDescriptionBlur(item);
    } else if (e.key === 'Escape') {
      setEditingDescriptionId(null);
      setEditingDescriptionValue('');
    }
  };

  const handleKeywordDoubleClick = (item, keywordIndex, currentKeyword) => {
    setEditingKeywordItem({ itemId: item.id, keywordIndex });
    setEditingKeywordValue(currentKeyword.trim());
  };

  const handleKeywordBlur = async (item, keywordIndex) => {
    if (!editingKeywordItem || editingKeywordItem.itemId !== item.id || editingKeywordItem.keywordIndex !== keywordIndex) {
      return;
    }

    const keywordsArray = item.keywords.split(',').map((k) => k.trim());
    const oldKeyword = keywordsArray[keywordIndex];
    const newKeywordValue = editingKeywordValue.trim();

    if (newKeywordValue && newKeywordValue !== oldKeyword) {
      keywordsArray[keywordIndex] = newKeywordValue;
      const updatedKeywords = keywordsArray.join(', ');
      await handleUpdateKeywords(item.id, updatedKeywords);
    }

    setEditingKeywordItem(null);
    setEditingKeywordValue('');
  };

  const handleKeywordKeyDown = (e, item, keywordIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleKeywordBlur(item, keywordIndex);
    } else if (e.key === 'Escape') {
      setEditingKeywordItem(null);
      setEditingKeywordValue('');
    }
  };

  const handleDeleteKeyword = async (id) => {
    try {
      setSaving(true);
      setError(null);

      await apiClient.delete(`/api/admin/keywords-admin/${id}`);
      fetchKeywords(selectedRole);
    } catch (err) {
      console.error('Error deleting keyword:', err);
      setError('Erreur lors de la suppression.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveKeywordTag = (item, keywordToRemove) => {
    const updatedKeywords = item.keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k !== keywordToRemove)
      .join(', ');

    handleUpdateKeywords(item.id, updatedKeywords);
  };

  const handleAddKeywordTag = async (item) => {
    if (!existingItemKeyword.trim()) return;

    const existingKeywords = item.keywords ? item.keywords.split(',').map((k) => k.trim()) : [];
    const updatedKeywords = [...existingKeywords, existingItemKeyword.trim()].join(', ');

    try {
      await handleUpdateKeywords(item.id, updatedKeywords);
      const currentId = item.id;
      setExistingItemKeyword('');
      setEditingId(currentId);

      setTimeout(() => {
        const input = document.getElementById(`keyword-input-${currentId}`);
        if (input) {
          input.focus();
        }
      }, 100);
    } catch (err) {
      console.error('Error adding keyword tag:', err);
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(keywords);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    const updated = reordered.map((item, idx) => ({ ...item, rank: idx }));
    setKeywords(updated);
    for (const item of updated) {
      await apiClient.patch(`/api/admin/keywords-admin/${item.id}/rank`, { rank: item.rank });
    }
    fetchKeywords(selectedRole);
  };

  if (rolesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} data-section="keywords">
      <Typography variant="h4" gutterBottom>
        Paramétrage des mots-clés
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Autocomplete
              options={roles}
              value={selectedRole || null}
              onChange={(_, value) => handleRoleChange(value || '')}
              getOptionLabel={(role) => getRoleLabel(role)}
              isOptionEqualToValue={(option, value) => option === value}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField {...params} label="Rôle à éditer" size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  alignItems: { xs: 'stretch', sm: 'center' },
                  width: '100%',
                }}
              >
                <Box sx={{ flex: 3, minWidth: 0 }}>
                  <Autocomplete
                    options={users}
                    value={selectedUser}
                    onChange={(_, value) => setSelectedUser(value)}
                    getOptionLabel={(user) => getUserLabel(user)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    loading={usersLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Appliquer à un utilisateur"
                        size="small"
                        placeholder="Rechercher un utilisateur..."
                      />
                    )}
                  />
                  {selectedUser && !selectedUser.clinic && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                      Cet utilisateur n&apos;a pas de clinique associée.
                    </Typography>
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleOpenApplyDialog}
                    disabled={!selectedUser || !selectedRole || !selectedUser?.clinic || applying || applyingToAll}
                    sx={{
                      bgcolor: 'rgb(141, 212, 209)',
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'rgb(121, 192, 189)' },
                    }}
                  >
                    Appliquer
                  </Button>
                </Box>
              </Box>

              <Box sx={{ width: '100%' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={handleOpenApplyAllDialog}
                  disabled={!selectedRole || eligibleUsersForRole.length === 0 || applying || applyingToAll}
                >
                  Appliquer à tous
                </Button>
                {selectedRole && eligibleUsersForRole.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Aucun utilisateur avec le rôle {getRoleLabel(selectedRole)} et une clinique associée.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {keywordsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Description"
                    value={editingDescription}
                    onChange={(e) => setEditingDescription(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, () => {
                      if (editingDescription && newKeyword) {
                        handleAddKeyword();
                      }
                    })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm mb-2"
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder="Mot clé"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, () => {
                        if (editingDescription && newKeyword) {
                          handleAddKeyword();
                        }
                      })}
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                    <button
                      onClick={() => handleAddKeyword()}
                      disabled={saving || !editingDescription || !newKeyword}
                      className={`inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium text-gray-700 rounded-md bg-[rgb(141,212,209)] ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(141,212,209)] disabled:bg-[rgb(141,212,209)] disabled:bg-opacity-50 disabled:text-gray-500`}
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Ajouter
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="keywords-list">
                  {(provided) => (
                    <ul
                      className="divide-y divide-gray-200"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {keywords
                        .slice()
                        .sort((a, b) => a.rank - b.rank)
                        .map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                            {(provided, snapshot) => (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`p-4 bg-white ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 flex items-center gap-2">
                                    <span {...provided.dragHandleProps} className="cursor-grab flex items-center mr-2">
                                      <Bars3Icon className="h-5 w-5 text-gray-400" />
                                    </span>
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                                        <span className="inline-block w-6 text-xs text-gray-400">#{item.rank + 1}</span>
                                        {editingDescriptionId === item.id ? (
                                          <input
                                            type="text"
                                            value={editingDescriptionValue}
                                            onChange={(e) => setEditingDescriptionValue(e.target.value)}
                                            onBlur={() => handleDescriptionBlur(item)}
                                            onKeyDown={(e) => handleDescriptionKeyDown(e, item)}
                                            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            autoFocus
                                          />
                                        ) : (
                                          <span
                                            onDoubleClick={() => handleDescriptionDoubleClick(item)}
                                            className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                            title="Double-cliquez pour modifier"
                                          >
                                            {item.description}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {item.keywords.split(',').map((keyword, idx) => (
                                          editingKeywordItem?.itemId === item.id && editingKeywordItem?.keywordIndex === idx ? (
                                            <input
                                              key={idx}
                                              type="text"
                                              value={editingKeywordValue}
                                              onChange={(e) => setEditingKeywordValue(e.target.value)}
                                              onBlur={() => handleKeywordBlur(item, idx)}
                                              onKeyDown={(e) => handleKeywordKeyDown(e, item, idx)}
                                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border-2 border-blue-500"
                                              autoFocus
                                              style={{ minWidth: '80px' }}
                                            />
                                          ) : (
                                            <span
                                              key={idx}
                                              onDoubleClick={() => handleKeywordDoubleClick(item, idx, keyword)}
                                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
                                              title="Double-cliquez pour modifier"
                                            >
                                              {keyword.trim()}
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRemoveKeywordTag(item, keyword.trim());
                                                }}
                                                className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-300 hover:text-blue-600 focus:outline-none"
                                              >
                                                <XMarkIcon className="h-3 w-3" />
                                              </button>
                                            </span>
                                          )
                                        ))}
                                        {(!editingKeywordItem || editingKeywordItem.itemId !== item.id) && (
                                          <div className="inline-flex items-center">
                                            <input
                                              id={`keyword-input-${item.id}`}
                                              type="text"
                                              placeholder="Ajouter plus"
                                              value={editingId === item.id ? existingItemKeyword : ''}
                                              onChange={(e) => {
                                                setEditingId(item.id);
                                                setExistingItemKeyword(e.target.value);
                                              }}
                                              onFocus={() => setEditingId(item.id)}
                                              onKeyDown={(e) => handleKeyDown(e, () => {
                                                if (existingItemKeyword) {
                                                  handleAddKeywordTag(item);
                                                }
                                              })}
                                              className="w-24 text-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                            <button
                                              onClick={() => handleAddKeywordTag(item)}
                                              disabled={saving || !existingItemKeyword}
                                              className="ml-1 p-1 text-gray-400 hover:text-gray-500"
                                            >
                                              <PlusIcon className="h-4 w-4" />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteKeyword(item.id)}
                                    className="ml-4 text-gray-400 hover:text-gray-500"
                                  >
                                    <XMarkIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              </li>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
            {successMessage}
          </div>
        )}

      {openRoleErrorDialog && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Rôle incompatible
              </h3>
            </div>

            <div className="px-6 py-4">
              <p className="text-sm text-gray-700">
                L&apos;utilisateur <strong>{getUserLabel(selectedUser)}</strong> n&apos;a pas le rôle{' '}
                <strong>{getRoleLabel(selectedRole)}</strong>.
              </p>
              <p className="mt-3 text-sm text-gray-500">
                {selectedUser.group
                  ? <>Rôle actuel de l&apos;utilisateur : <strong>{getRoleLabel(selectedUser.group)}</strong>.</>
                  : 'Aucun rôle n\'est défini pour cet utilisateur.'}
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-lg">
              <button
                type="button"
                onClick={handleCloseRoleErrorDialog}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {openApplyAllDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg max-w-lg w-full border-2 border-red-300">
            <div className="px-6 py-4 border-b border-red-200 bg-red-50">
              <h3 className="text-lg font-medium text-red-800">
                Attention
              </h3>
            </div>

            <div className="px-6 py-4">
              <p className="text-sm text-red-700 font-medium">
                Vous êtes sur le point d&apos;appliquer les mots-clés du rôle{' '}
                <strong>{getRoleLabel(selectedRole)}</strong> à tous les utilisateurs concernés.
              </p>
              <p className="mt-3 text-sm text-gray-700">
                Cette action va <strong>écraser les mots-clés existants</strong> de{' '}
                <strong>{eligibleUsersForRole.length} utilisateur(s)</strong> ayant ce rôle.
              </p>
              <p className="mt-3 text-sm text-gray-500">
                Cette opération est irréversible. Vérifiez bien le rôle sélectionné avant de confirmer.
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
              <button
                type="button"
                onClick={handleCloseApplyAllDialog}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleApplyKeywordsToAll}
                disabled={applyingToAll}
                className={`inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white ${!applyingToAll ? 'hover:bg-red-700' : 'opacity-75 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2`}
              >
                {applyingToAll ? 'Application...' : 'Confirmer l\'application à tous'}
              </button>
            </div>
          </div>
        </div>
      )}

      {openApplyDialog && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Appliquer les mots-clés
              </h3>
            </div>

            <div className="px-6 py-4">
              <p className="text-sm text-gray-500">
                L&apos;application des mots-clés du rôle <strong>{getRoleLabel(selectedRole)}</strong> va
                écraser les mots-clés existants de cet utilisateur pour ce rôle.
              </p>
              <p className="mt-3 text-sm text-gray-500">
                Cette action est irréversible.
              </p>
              <p className="mt-3 text-sm text-gray-700">
                Utilisateur concerné : <strong>{getUserLabel(selectedUser)}</strong>
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
              <button
                type="button"
                onClick={handleCloseApplyDialog}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleApplyKeywordsToUser}
                disabled={applying}
                className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 ${!applying ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
              >
                {applying ? 'Application...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}
